/** biome-ignore-all lint/suspicious/noConsole: console for debugging */
import { useEffect } from 'react';

const DEFAULT_CHANNEL = 'drifti';
type BroadcastMessage = 'switched-account' | 'logout';

const createBroadcastChannel = (channel = DEFAULT_CHANNEL) => {
  // Some browsers don't support BroadcastChannel, like Safari 13.1.2
  if (typeof BroadcastChannel === 'undefined') {
    console.warn('BroadcastChannel is not supported in this browser.');
    return;
  }

  return new BroadcastChannel(channel);
};

/**
 * Where this tab goes after the next account switch, instead of the app's start page. Per tab, so
 * the other tabs a switch reloads still start over.
 */
const SWITCH_RETURN_TO = 'drifti:switch-account-return-to';

export const setSwitchAccountReturnTo = (url: string | null) => {
  try {
    if (url) sessionStorage.setItem(SWITCH_RETURN_TO, url);
    else sessionStorage.removeItem(SWITCH_RETURN_TO);
  } catch {
    // Storage blocked: the switch lands on the start page, as it did before.
  }
};

const takeSwitchAccountReturnTo = () => {
  try {
    const url = sessionStorage.getItem(SWITCH_RETURN_TO);
    sessionStorage.removeItem(SWITCH_RETURN_TO);
    return url;
  } catch {
    return null;
  }
};

export const broadcast = (msg: BroadcastMessage, channel = DEFAULT_CHANNEL) => {
  const bc = createBroadcastChannel(channel);
  if (!bc) return;
  bc.postMessage(msg);
  bc.close();
};

export const useBroadcastListener = (channel = DEFAULT_CHANNEL) => {
  useEffect(() => {
    const bc = createBroadcastChannel(channel);
    if (!bc) return;

    const messageHandler = (e: MessageEvent<BroadcastMessage>) => {
      switch (e.data) {
        case 'switched-account': {
          const [, app] = window.location.pathname.split('/');
          if (app === 'superadmin') return; // No need to restart superadmin after account switch
          window.location.href = takeSwitchAccountReturnTo() ?? `/${app}`;
          break;
        }

        case 'logout': {
          // No `next`: whoever signs in after a sign-out should not land on the previous user's page.
          window.location.href = '/auth';
          break;
        }

        default: {
          console.warn('Unhandled broadcast message', e.data);
        }
      }
    };

    bc.addEventListener('message', messageHandler);

    return () => {
      bc.close();
      bc.removeEventListener('message', messageHandler);
    };
  }, [channel]);
};
